// Runtime dispatcher keeps logger code out of the core bundle until a log is needed.
// It routes resolved runtime events into lazy browser sinks or emergency fallback.
import { emitLentyConsoleLog, resolveLentyLogEvent, type LentyLogEvent } from '@lentystyle/guard/shared'
import { resolveRuntimeScriptIntegrity } from '@lentystyle/guard/runtime'
import { isRuntimeMinifiedBuild, resolveRuntimeAssetUrl } from '../features/assets'
import { buildRuntimeSecurityViolation, emitRuntimeSecurityViolation } from '../security-events'
import { createTrustedRuntimeScriptUrl } from '../trusted-types'
import { drainRuntimeLogQueue, enqueueRuntimeLog, hasRuntimeLogSink, type RuntimeLogLayer } from './store'

const runtimeLogLoads = new Map<RuntimeLogLayer, Promise<boolean>>()
const runtimeLogFailures = new Set<RuntimeLogLayer>()
const LENTY_LOG_DISPATCH_KEY = '__LentyStyleDispatchLogEvent__'

// Install the runtime-specific log dispatcher so shared emit calls can lazy-load sinks.
export function installRuntimeLogDispatcher(): void {
  const runtimeGlobal = globalThis as typeof globalThis & { [LENTY_LOG_DISPATCH_KEY]?: (event: LentyLogEvent) => boolean }
  // Keep one stable dispatcher registration for the current runtime bundle.
  if (runtimeGlobal[LENTY_LOG_DISPATCH_KEY]) {
    return
  }

  runtimeGlobal[LENTY_LOG_DISPATCH_KEY] = dispatchRuntimeLogEvent
}

// Route runtime events into the correct browser sink and leave other scopes untouched.
function dispatchRuntimeLogEvent(event: LentyLogEvent): boolean {
  // Leave non-runtime scopes for direct console emission in other environments.
  if (event.scope !== 'runtime') {
    return false
  } else if (event.layer === 'critical' || event.layer === 'optional') {
    dispatchRuntimeLayer(event.layer, event)
    return true
  } else {
    emitLentyConsoleLog(event)
    return true
  }
}

// Queue or deliver the current runtime event and start sink loading if needed.
function dispatchRuntimeLayer(layer: RuntimeLogLayer, event: LentyLogEvent): void {
  // Deliver directly when the current layer sink already exists.
  if (hasRuntimeLogSink(layer)) {
    enqueueRuntimeLog(layer, event)
    return
  }

  // Fall back immediately after a previous bundle-load failure for this layer.
  if (runtimeLogFailures.has(layer)) {
    emitLentyConsoleLog(event)
    return
  }

  enqueueRuntimeLog(layer, event)
  void ensureRuntimeLogLayer(layer)
}

// Reuse or start the current sink-load promise for the requested layer.
async function ensureRuntimeLogLayer(layer: RuntimeLogLayer): Promise<void> {
  // Stop when the current layer already failed or registered a sink.
  if (runtimeLogFailures.has(layer) || hasRuntimeLogSink(layer)) {
    return
  }

  const existingLoad = runtimeLogLoads.get(layer)
  // Reuse the current load promise while one sink bundle is already loading.
  if (existingLoad) {
    await existingLoad
    return
  }

  const loadPromise = loadRuntimeLogLayer(layer)
  runtimeLogLoads.set(layer, loadPromise)
  const loaded = await loadPromise
  runtimeLogLoads.delete(layer)
  if (loaded) {
    return
  }

  runtimeLogFailures.add(layer)
  emitLentyConsoleLog(resolveLentyLogEvent('RUN0008', { layer }))
  flushRuntimeLogLayerToFallback(layer)
}

// Inject one browser log bundle and resolve only after it registers a sink.
async function loadRuntimeLogLayer(layer: RuntimeLogLayer): Promise<boolean> {
  const minSuffix = isRuntimeMinifiedBuild() ? '.min' : ''
  const scriptUrl = resolveRuntimeAssetUrl(`lentystyle.log-${layer}${minSuffix}.js`)
  // Stop bundle injection when the runtime asset url cannot be resolved.
  if (!scriptUrl) {
    emitRuntimeSecurityViolation(buildRuntimeSecurityViolation(
      'runtime-script-rejected',
      'Runtime log bundle URL could not be resolved or was blocked.',
      { feature: `log-${layer}` },
    ))
    return false
  }

  const resolvedUrl = new URL(scriptUrl, document.baseURI)
  const integrity = await resolveRuntimeScriptIntegrity(scriptUrl, document.baseURI)
  const isCrossOrigin = resolvedUrl.origin !== window.location.origin
  // Block cross-origin logger bundles when integrity metadata is unavailable.
  if (isCrossOrigin && !integrity) {
    emitRuntimeSecurityViolation(buildRuntimeSecurityViolation(
      'runtime-script-rejected',
      'Cross-origin runtime log bundle was blocked without integrity metadata.',
      { feature: `log-${layer}`, href: scriptUrl },
    ))
    return false
  }

  return new Promise<boolean>((resolve) => {
    const script = document.createElement('script')
    // Trusted Types returns a browser URL object while TS still exposes `src` as string.
    script.src = createTrustedRuntimeScriptUrl(scriptUrl) as string
    script.async = true
    script.dataset.luisRuntimeLog = layer
    if (integrity) {
      script.integrity = integrity
      script.crossOrigin = 'anonymous'
    }
    script.onload = () => resolve(hasRuntimeLogSink(layer))
    script.onerror = () => resolve(false)
    document.head.appendChild(script)
  })
}

// Flush queued runtime events through the emergency console fallback.
function flushRuntimeLogLayerToFallback(layer: RuntimeLogLayer): void {
  const drained = drainRuntimeLogQueue(layer)
  for (const event of drained) {
    emitLentyConsoleLog(event)
  }
}
